/**
 * Vincent #124 (Eric 2026-05-13) — Tableau de bord Diagnostics admin.
 *
 * Affiche les `client_errors` récents — crashes React + diagnostics de
 * connexion auto-envoyés depuis le panneau "Connexion temporairement
 * indisponible". Permet aux admins (Eric/Vincent) de voir en quasi-temps
 * réel ce qui bloque pour un utilisateur en production.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  Stethoscope, ArrowClockwise, Funnel, CheckCircle, XCircle, Warning,
  Clock, Browsers, Globe, User as UserIcon, Database, Play, SpinnerGap,
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";
import ColorblindTester from "../components/ColorblindTester";
// Vincent B796 backlog (Eric 2026-02-12) — Diagnostic rate-limit 429.
import RateLimitDiagnosticSection from "../components/admin/RateLimitDiagnosticSection";

const KIND_LABELS = {
  fr: {
    react_crash: "Crash React",
    auth_unreachable: "Connexion injoignable",
    "": "Inconnu",
  },
  en: {
    react_crash: "React crash",
    auth_unreachable: "Connection unreachable",
    "": "Unknown",
  },
};

const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-800 border-red-300",
  warning: "bg-amber-100 text-amber-800 border-amber-300",
  success: "bg-teal-100 text-teal-800 border-teal-300",
  unknown: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `il y a ${Math.floor(diff)}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return d.toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
}

function shortBrowser(ua) {
  if (!ua) return "?";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari";
  return "Autre";
}

export default function AdminDiagnosticsPage() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("auth_unreachable");
  const [expanded, setExpanded] = useState({});

  // Vincent #132 (Eric 2026-05-13) — Migrations panel state.
  const [migrations, setMigrations] = useState([]);
  const [migHistory, setMigHistory] = useState([]);
  const [migLoading, setMigLoading] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const loadMigrations = useCallback(async () => {
    setMigLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/migrations`, { withCredentials: true });
      setMigrations(Array.isArray(data?.items) ? data.items : []);
      setMigHistory(Array.isArray(data?.history) ? data.history : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec du chargement" : "Load failed"));
    } finally { setMigLoading(false); }
  }, [lang]);

  const runMigration = async (id) => {
    const ok = await confirmDialog(lang === "fr" ? `Relancer "${id}" ? Action idempotente.` : `Re-run "${id}"? Idempotent.`);
    if (!ok) return;
    setRunningId(id);
    try {
      const { data } = await axios.post(`${API}/api/admin/migrations/${id}/run`, {}, { withCredentials: true });
      if (data?.status === "success") {
        toast.success(`${id} — ${JSON.stringify(data.stats || {})}`);
      } else {
        toast.error(`${id} — ${data?.error || "erreur"}`);
      }
      await loadMigrations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setRunningId(null); }
  };
  useEffect(() => { loadMigrations(); }, [loadMigrations]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/client-errors`, {
        params: { limit: 200, kind: kindFilter || undefined },
        withCredentials: true,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du chargement");
      setItems([]);
    } finally { setLoading(false); }
  }, [kindFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = lang === "fr" ? "Diagnostics — Knoria" : "Diagnostics — Knoria"; }, [lang]);

  const stats = useMemo(() => {
    const last24h = items.filter((it) => {
      const ts = new Date(it.server_ts || 0).getTime();
      return Date.now() - ts < 86400000;
    });
    const uniqueEmails = new Set(items.map((i) => i.user_email).filter(Boolean));
    const auth = items.filter((i) => i.kind === "auth_unreachable").length;
    const crash = items.filter((i) => i.kind === "react_crash").length;
    return { total: items.length, last24h: last24h.length, users: uniqueEmails.size, auth, crash };
  }, [items]);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-diagnostics-page">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
          <Stethoscope size={26} weight="bold" />
          {lang === "fr" ? "Diagnostics — Rapports utilisateurs" : "Diagnostics — User reports"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? "Tous les rapports auto-envoyés depuis le panneau \"Connexion temporairement indisponible\" + crashs React. Utile pour comprendre ce qui bloque pour Vincent, Jean-Sébastien ou un client en cas d'incident."
            : "All auto-sent reports from the \"Connection unavailable\" panel + React crashes. Useful to debug what's blocking users."}
        </p>
      </div>

      {/* Testeur daltonisme — Vincent 2026-07-25 */}
      <ColorblindTester />

      {/* Vincent B796 backlog (Eric 2026-02-12) — Diagnostic rate-limit 429.
          Placé en haut car c'est un signal opérationnel court terme
          (fenêtre 15 min) plus urgent que les stats agrégées 24h. */}
      <RateLimitDiagnosticSection lang={lang} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="diag-stat-total">
          <div className="text-[10px] uppercase text-zinc-500 font-bold">Total</div>
          <div className="text-2xl font-bold text-zinc-900">{stats.total}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="diag-stat-24h">
          <div className="text-[10px] uppercase text-zinc-500 font-bold">{lang === "fr" ? "24 dern. h" : "Last 24h"}</div>
          <div className="text-2xl font-bold text-amber-600">{stats.last24h}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="diag-stat-users">
          <div className="text-[10px] uppercase text-zinc-500 font-bold">{lang === "fr" ? "Utilisateurs uniques" : "Unique users"}</div>
          <div className="text-2xl font-bold text-blue-600">{stats.users}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="diag-stat-auth">
          <div className="text-[10px] uppercase text-zinc-500 font-bold">{lang === "fr" ? "Connexion" : "Connection"}</div>
          <div className="text-2xl font-bold text-red-600">{stats.auth}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="diag-stat-crash">
          <div className="text-[10px] uppercase text-zinc-500 font-bold">Crash React</div>
          <div className="text-2xl font-bold text-rose-600">{stats.crash}</div>
        </div>
      </div>


      {/* Vincent B982 (Eric 2026-02-26 SPEC STRICK PROD) — Panel
          « Backfill procédures » DÉPLACÉ vers /dashboard/admin/procedures-regeneration
          (nouvelle entrée sidebar « Procédures : regénération massive »
          sous Concepteurs > Génération de connaissances). */}

      {/* Vincent #132 (Eric 2026-05-13) — Panel Migrations */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-6" data-testid="migrations-panel">
        <div className="flex items-center gap-2 mb-3">
          <Database size={18} weight="bold" className="text-[#002FA7]" />
          <h2 className="text-lg font-bold text-zinc-900">{lang === "fr" ? "Migrations & re-traitement" : "Migrations & reprocess"}</h2>
          <button onClick={loadMigrations} className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-300 rounded hover:bg-zinc-50" data-testid="migrations-refresh-btn">
            <ArrowClockwise size={12} weight="bold" />{migLoading ? "…" : (lang === "fr" ? "Rafraîchir" : "Refresh")}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">
          {lang === "fr"
            ? "Les migrations rétro-appliquent les nouvelles fonctionnalités aux données existantes. Exécutées automatiquement au démarrage du serveur (idempotent). Bouton manuel pour les relancer ad-hoc."
            : "Migrations retro-apply new features to existing data. Auto-executed on server boot (idempotent). Manual button for ad-hoc re-runs."}
        </p>
        <ul className="space-y-1.5" data-testid="migrations-list">
          {migrations.map((m) => {
            const last = m.last_run || {};
            const ok = last.status === "success";
            const err = last.status === "error";
            return (
              <li key={m.migration_id} className="border border-zinc-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 bg-zinc-50/30" data-testid={`migration-${m.migration_id}`}>
                {ok && <CheckCircle size={16} weight="fill" className="text-teal-600 shrink-0" />}
                {err && <XCircle size={16} weight="fill" className="text-red-600 shrink-0" />}
                {!ok && !err && <Clock size={16} weight="bold" className="text-zinc-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-mono text-zinc-900 truncate">{m.migration_id}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.description}</div>
                  {last.stats && Object.keys(last.stats).length > 0 && (
                    <div className="text-[11px] text-zinc-700 mt-0.5">
                      <span className="font-semibold">Stats :</span> {Object.entries(last.stats).map(([k, v]) => `${k}=${v}`).join(" · ")}
                    </div>
                  )}
                  {err && last.error && (
                    <div className="text-[11px] text-red-700 mt-0.5 truncate" title={last.error}>{last.error}</div>
                  )}
                </div>
                {last.started_at && (
                  <span className="text-[10px] text-zinc-400 whitespace-nowrap">
                    {new Date(last.started_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                )}
                <button
                  onClick={() => runMigration(m.migration_id)}
                  disabled={runningId === m.migration_id}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#002FA7] text-white rounded hover:bg-[#001f7a] disabled:opacity-50"
                  data-testid={`run-migration-${m.migration_id}`}
                >
                  {runningId === m.migration_id ? <SpinnerGap size={12} className="animate-spin" /> : <Play size={12} weight="fill" />}
                  {runningId === m.migration_id ? "…" : (lang === "fr" ? "Relancer" : "Run")}
                </button>
              </li>
            );
          })}
          {migrations.length === 0 && !migLoading && (
            <li className="text-xs text-zinc-500 text-center py-4">{lang === "fr" ? "Aucune migration enregistrée." : "No migrations registered."}</li>
          )}
        </ul>
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Funnel size={14} className="text-zinc-500" />
        {[
          { id: "", label: lang === "fr" ? "Tous" : "All" },
          { id: "auth_unreachable", label: lang === "fr" ? "Connexion" : "Connection" },
          { id: "react_crash", label: "Crash React" },
        ].map((o) => (
          <button key={o.id || "all"} onClick={() => setKindFilter(o.id)}
            className={`px-3 py-1.5 text-xs rounded-md border ${kindFilter === o.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
            data-testid={`diag-filter-${o.id || "all"}`}>{o.label}</button>
        ))}
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-zinc-300 rounded-md bg-white hover:bg-zinc-50" data-testid="diag-refresh-btn">
          <ArrowClockwise size={12} weight="bold" /> {lang === "fr" ? "Rafraîchir" : "Refresh"}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500">{lang === "fr" ? "Chargement…" : "Loading…"}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-zinc-200 rounded-xl">
          <CheckCircle size={48} weight="duotone" className="mx-auto mb-3 text-teal-400" />
          <p className="font-semibold text-zinc-700">{lang === "fr" ? "Aucun rapport — tout va bien !" : "No reports — all clear!"}</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="diag-list">
          {items.map((it, idx) => {
            const isOpen = !!expanded[idx];
            const sev = it.summary?.severity || (it.kind === "react_crash" ? "critical" : "unknown");
            return (
              <li key={idx} className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid={`diag-item-${idx}`}>
                <button onClick={() => setExpanded((p) => ({ ...p, [idx]: !p[idx] }))} className="w-full text-left px-4 py-3 hover:bg-zinc-50">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-bold ${SEVERITY_COLORS[sev]}`}>
                      {(KIND_LABELS[lang] || KIND_LABELS.fr)[it.kind] || it.kind}
                    </span>
                    {it.user_email && (
                      <span className="text-xs text-zinc-700 inline-flex items-center gap-1"><UserIcon size={11} />{it.user_email}</span>
                    )}
                    <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
                      <Clock size={11} /> {fmtRel(it.server_ts)}
                    </span>
                    <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
                      <Browsers size={11} /> {shortBrowser(it.user_agent)}
                    </span>
                    {it.cf_country && (
                      <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
                        <Globe size={11} /> {it.cf_country}
                      </span>
                    )}
                    <span className="ml-auto text-[11px] text-zinc-400">{isOpen ? "▲" : "▼"}</span>
                  </div>
                  <div className="text-xs text-zinc-700 mt-1 truncate">
                    {it.summary?.title || it.message || it.url || "—"}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 py-3 border-t border-zinc-100 bg-zinc-50 text-xs space-y-2" data-testid={`diag-detail-${idx}`}>
                    {it.summary && (
                      <div>
                        <div className="font-semibold text-zinc-700">Cause probable :</div>
                        <div className="text-zinc-600">{it.summary.cause}</div>
                      </div>
                    )}
                    {Array.isArray(it.attempts) && it.attempts.length > 0 && (
                      <div>
                        <div className="font-semibold text-zinc-700 mb-1">Tests réseau :</div>
                        <div className="space-y-1">
                          {it.attempts.map((a, i) => (
                            <div key={i} className="flex items-center gap-2 font-mono text-[11px] bg-white px-2 py-1 rounded border border-zinc-200">
                              {a.ok ? <CheckCircle size={12} weight="fill" className="text-teal-600" /> : <XCircle size={12} weight="fill" className="text-red-600" />}
                              <span className="flex-1 truncate">{a.label || a.url}</span>
                              <span className="text-zinc-500">{a.status != null ? `HTTP ${a.status}` : (a.error || "—")}</span>
                              {a.ms != null && <span className="text-zinc-400">{a.ms}ms</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {it.nav && Object.keys(it.nav).length > 0 && (
                      <div className="text-[11px] text-zinc-600">
                        <span className="font-semibold">Navigateur :</span> onLine={String(it.nav.onLine)} · cookieEnabled={String(it.nav.cookieEnabled)} · {it.nav.language} · {it.nav.platform}
                      </div>
                    )}
                    {it.message && it.kind === "react_crash" && (
                      <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-red-800 font-mono text-[11px]">
                        <Warning size={11} weight="bold" className="inline mr-1" />
                        {it.message}
                      </div>
                    )}
                    <div className="text-[10px] text-zinc-400">
                      IP {it.ip || "—"} · CF-Ray {it.cf_ray || "—"} · URL {it.url || "—"}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
